import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useReducer } from 'react';
import { Redirect, Route, useHistory, useLocation } from 'react-router-dom';
import { FunctionalLoader, useDidUpdate, Page, PageContentHeader, Typography, useStateInRef } from 'seagull';
import ErrorBoundaryPage from '~/components/ErrorBoundaryPage/ErrorBoundaryPage';
import FeatureFlagsRelationshipContext from '~/components/FeatureFlagsRelationshipContext';
import { routes } from '~/constants';
import { useOverlayContext, useStore } from '~/contexts';
import { useRelationship } from '~/hooks/reactQuery/queries/useRelationship.tsx';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { relationshipModel, relationshipSummaryModel } from '~/models';
import { AdHocAgreementEmptyState } from '~/pages/GlobalBillingHub/AdHocAgreementEmptyState/AdHocAgreementEmptyState';
import { BIRelationshipScopeProvider } from '~/providers/BIRelationshipScopeProvider';
import { selectors } from '~/store/state-selectors';
import { FilterType } from '../AgreementsDashboard/agreementsDashboardFilters';
import ActivityLog from './ActivityLog/ActivityLog';
import styles from './AgreementMinisite.module.scss';
import AgreementMinisiteTopBar from './AgreementMinisiteTopBar/AgreementMinisiteTopBar';
import AgreementView from './AgreementView/AgreementView';
import ApproveAllBar from './ApproveAll/ApproveAllBar';
import BillingHub from './BillingHub/BillingHub';
import InvoicesView from './InvoicesView/InvoicesView';
import PaymentMethods from './PaymentMethods/PaymentMethods';
export const buildMinisiteTabs = (t)=>({
        billingHub: {
            url: 'billing-hub',
            label: t.billingHub(),
            supportsSample: false
        },
        agreement: {
            url: 'agreement',
            label: t.agreement(),
            supportsSample: true
        },
        paymentMethods: {
            url: 'payment-methods',
            label: t.paymentMethods(),
            supportsSample: false
        },
        invoices: {
            url: 'invoices',
            label: t.invoices(),
            supportsSample: false
        },
        activity: {
            url: 'activity',
            label: t.activityLog(),
            supportsSample: false
        }
    });
function ActualMinisite(props) {
    const { relationship, refresh, match } = props;
    const { t } = useLocaleKeys('pages.agreementMinisite');
    const minisiteTabs = buildMinisiteTabs(t.tabs);
    const getProps = useStateInRef(props);
    const location = useLocation();
    const numAmendments = relationshipModel.numAmendments(relationship);
    const numInvoicesRequiringApproval = relationshipModel.numInvoicesRequiringApproval(relationship);
    const amountOfPendingCharges = relationshipModel.numPendingCharges(relationship);
    const userIsVendor = useStore(selectors.user.isVendorInRelationship(relationship));
    const history = useHistory();
    const locationStack = useStore(selectors.locationStack);
    const overlayContext = useOverlayContext();
    const scrollContainerRef = useRef(null);
    const [dismissedApproveAll, setDismissedApproveAll] = useState(false);
    const [tabRefreshKey, remountCurrentTab] = useReducer((state)=>state + 1, 0);
    const [billingHubKey, agreementViewKey, paymentMethodsKey, invoicesViewKey, activityLogKey] = new Array(5).fill(tabRefreshKey).map((val, i)=>val + i);
    const scrollElRef = useRef(null);
    const relationshipScope = {
        id: relationship.id,
        isApproved: relationshipModel.isApproved(relationship)
    };
    const shouldShowApproveAllModal = !dismissedApproveAll && !userIsVendor && relationshipModel.isApproved(relationship) && numAmendments + numInvoicesRequiringApproval + amountOfPendingCharges > 1;
    const currentTabUrl = match.path === location.pathname ? null : (location.pathname.match(new RegExp(match.url + '/([^/]*)')) || {})[1];
    useDidUpdate(()=>{
        const el = scrollElRef.current;
        if (el) {
            el.scrollTop = 0;
        }
    }, [
        location.pathname
    ]);
    useDidUpdate(()=>{
        getProps().refresh();
    }, [
        match.params.relationshipId
    ]);
    const currentTab = Object.keys(minisiteTabs).find((tabName)=>minisiteTabs[tabName].url === currentTabUrl);
    useOnMount(()=>{
        var _location_state;
        if (!currentTab) {
            const path = relationshipModel.isApproved(relationship) || relationshipModel.isAdHoc(relationship) ? minisiteTabs.billingHub.url : minisiteTabs.agreement.url;
            history.replace("".concat(match.url, "/").concat(path));
        }
        if (((_location_state = location.state) === null || _location_state === void 0 ? void 0 : _location_state.scrollToBottom) && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
    });
    const agreementUrl = "".concat(match.url, "/").concat(minisiteTabs.agreement.url);
    const paymentMethodsUrl = "".concat(match.url, "/").concat(minisiteTabs.paymentMethods.url);
    const displayedTitle = relationshipModel.displayedTitle(relationship, userIsVendor);
    return /*#__PURE__*/ _jsx(BIRelationshipScopeProvider, {
        relationshipScope: relationshipScope,
        children: /*#__PURE__*/ _jsx(Page, {
            testId: "minisite-page",
            ref: scrollElRef,
            header: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    relationshipModel.opposingPartyName(relationship, userIsVendor),
                    displayedTitle && /*#__PURE__*/ _jsxs(Typography, {
                        as: "span",
                        variant: "h4",
                        ellipsis: true,
                        children: [
                            "\xa0— ",
                            displayedTitle
                        ]
                    })
                ]
            }),
            onClickBack: ()=>{
                if (locationStack.length > 1) {
                    const lastLocation = locationStack[locationStack.length - 2];
                    if (lastLocation.pathname === routes.agreementsDashboard) {
                        history.goBack();
                        return;
                    }
                }
                if (relationshipModel.isApproved(relationship) || relationshipModel.isTerminated(relationship) || relationshipModel.isAdHoc(relationship)) {
                    const agreementStatusKey = FilterType.AgreementStatus;
                    const searchParams = relationshipModel.isTerminated(relationship) ? {
                        [agreementStatusKey]: relationshipSummaryModel.AgreementStatus.Terminated
                    } : {};
                    const search = "?".concat(new URLSearchParams(searchParams));
                    history.push({
                        pathname: routes.agreementsDashboard,
                        search
                    });
                } else {
                    history.push(routes.proposalsDashboard);
                }
            },
            children: /*#__PURE__*/ _jsx("div", {
                className: styles.main,
                "data-testid": "agreement-minisite",
                children: /*#__PURE__*/ _jsxs(ErrorBoundaryPage, {
                    bounded: true,
                    children: [
                        /*#__PURE__*/ _jsxs(PageContentHeader, {
                            children: [
                                shouldShowApproveAllModal && /*#__PURE__*/ _jsx(ApproveAllBar, {
                                    agreement: relationship,
                                    amendments: numAmendments,
                                    invoices: numInvoicesRequiringApproval,
                                    charges: amountOfPendingCharges,
                                    onClose: (param)=>{
                                        let { status } = param;
                                        if (status === 'success') {
                                            refresh();
                                            remountCurrentTab();
                                            overlayContext.showSuccessToast(t.approvedSuccessfully(), 'approve-all-success-toast');
                                            if (!shouldShowApproveAllModal) {
                                                setDismissedApproveAll(true);
                                            }
                                        } else if (status === 'error') {
                                            overlayContext.showErrorToast(t.errorOccurredContactVendor());
                                        } else if (status === 'dismissed') {
                                            setDismissedApproveAll(true);
                                        }
                                    }
                                }),
                                /*#__PURE__*/ _jsx(AgreementMinisiteTopBar, {
                                    relationship: relationship,
                                    tabs: minisiteTabs
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.errorBoundary,
                            children: /*#__PURE__*/ _jsxs(ErrorBoundaryPage, {
                                bounded: true,
                                children: [
                                    (!relationship.isSample || minisiteTabs.billingHub.supportsSample) && /*#__PURE__*/ _jsx(Route, {
                                        path: "".concat(match.url, "/").concat(minisiteTabs.billingHub.url),
                                        exact: true,
                                        render: (props)=>/*#__PURE__*/ _jsx(BillingHub, _object_spread({
                                                isVendor: userIsVendor,
                                                agreement: relationship,
                                                refreshAgreement: refresh
                                            }, props))
                                    }, billingHubKey),
                                    (!relationship.isSample || minisiteTabs.agreement.supportsSample) && /*#__PURE__*/ _jsx(Route, {
                                        path: agreementUrl,
                                        children: relationshipModel.isAdHoc(relationship) ? /*#__PURE__*/ _jsx(AdHocAgreementEmptyState, {
                                            clientContactId: relationship.clientContactId
                                        }) : /*#__PURE__*/ _jsx("div", {
                                            ref: scrollContainerRef,
                                            className: styles.content,
                                            "data-testid": 'agreement-minisite-agreement-tab',
                                            children: /*#__PURE__*/ _jsx(AgreementView, {
                                                relationship: relationship,
                                                isVendor: userIsVendor,
                                                refresh: refresh
                                            })
                                        })
                                    }, agreementViewKey),
                                    (!relationship.isSample || minisiteTabs.invoices.supportsSample) && /*#__PURE__*/ _jsx(Route, {
                                        path: "".concat(match.url, "/").concat(minisiteTabs.invoices.url),
                                        render: (props)=>/*#__PURE__*/ _jsx(InvoicesView, _object_spread({
                                                agreement: relationship,
                                                viewMode: userIsVendor ? 'vendor' : 'customer',
                                                refreshAgreement: refresh
                                            }, props), "invoices-view-".concat(dismissedApproveAll))
                                    }, invoicesViewKey),
                                    (!relationship.isSample || minisiteTabs.billingHub.supportsSample) && /*#__PURE__*/ _jsx(Route, {
                                        path: "".concat(match.url, "/").concat(minisiteTabs.activity.url),
                                        render: (props)=>/*#__PURE__*/ _jsx(ActivityLog, _object_spread({
                                                agreement: relationship
                                            }, props))
                                    }, activityLogKey),
                                    (!relationship.isSample || minisiteTabs.paymentMethods.supportsSample) && /*#__PURE__*/ _jsx(Route, {
                                        path: paymentMethodsUrl,
                                        render: ()=>/*#__PURE__*/ _jsx(PaymentMethods, {
                                                isVendor: userIsVendor,
                                                agreement: relationship,
                                                onRefreshAgreement: refresh
                                            })
                                    }, paymentMethodsKey)
                                ]
                            })
                        })
                    ]
                })
            })
        })
    });
}
export default function AgreementMinisite(props) {
    const { relationship, refresh, isLoading, error } = useRelationship(props.match.params.relationshipId, {
        refreshOnMount: true
    });
    const { AD_HOC_INVOICING: isAdHocInvoicingEnabled } = useFeatureFlags();
    const { t } = useLocaleKeys('pages.agreementMinisite');
    useSetDocumentTitle(t.documentTitle());
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(FunctionalLoader, {
                show: isLoading
            }),
            relationship && (relationshipModel.isApproved(relationship) || relationshipModel.isTerminated(relationship) || isAdHocInvoicingEnabled && relationshipModel.isAdHoc(relationship) ? /*#__PURE__*/ _jsx(FeatureFlagsRelationshipContext, {
                vendorId: relationship.vendor.id,
                children: /*#__PURE__*/ _jsx(ActualMinisite, _object_spread_props(_object_spread({}, props), {
                    relationship: relationship,
                    refresh: refresh
                }))
            }) : /*#__PURE__*/ _jsx(Redirect, {
                to: routes.home
            })),
            !!error && /*#__PURE__*/ _jsx(Redirect, {
                to: routes.home
            })
        ]
    });
}
